import { useEffect, useRef, useCallback } from 'react';

import { useAppDispatch, useAppSelector } from '@/shared/hooks';

import {
  ENCOUNTER_POLLING_INTERVAL_MS,
  type TranscriptorConsultation,
} from '../config';
import {
  selectHiddenTranscriptorConsultationsByPatientId,
  selectTranscriptorConsultationsByPatientId,
  selectTranscriptorConsultationsLoadedByPatientId,
  selectTranscriptorError,
  selectTranscriptorLoading,
  selectVisibleTranscriptorConsultationsTotalByPatientId,
} from '../model/transcriptorSlice.selectors';
import { fetchConsultations } from '../model/transcriptorSlice.thunks';

const ACTIVE_STATUSES = ['created', 'recording', 'processing'];
const MAX_FAILED_POLLS = 6; // keep polling ~30s after "failed" in case backend retries
// A stable fallback: an inline `[]` is a new reference on every render and
// trips react-redux's selector stability check.
const NO_CONSULTATIONS: TranscriptorConsultation[] = [];

// `transcriptorV2Enabled` is the flag of the patient's organization, undefined
// until its settings load. The list is read once; it counts as loaded only
// with the flag, so a Transcriptor v2 recording never shows and then hides.
export const useTranscriptorConsultations = (
  patientId: string | undefined,
  options: {
    includeHidden?: boolean;
    transcriptorV2Enabled: boolean | undefined;
  },
) => {
  const { transcriptorV2Enabled } = options;
  const includeHidden = Boolean(options.includeHidden);
  const dispatch = useAppDispatch();
  const storedConsultations = useAppSelector((state) =>
    patientId
      ? selectTranscriptorConsultationsByPatientId(state, patientId)
      : NO_CONSULTATIONS,
  );
  const storedHiddenConsultations = useAppSelector((state) =>
    patientId && includeHidden
      ? selectHiddenTranscriptorConsultationsByPatientId(state, patientId)
      : NO_CONSULTATIONS,
  );
  const visibleTotal = useAppSelector((state) =>
    patientId
      ? selectVisibleTranscriptorConsultationsTotalByPatientId(state, patientId)
      : 0,
  );
  const isListLoaded = useAppSelector((state) =>
    patientId
      ? selectTranscriptorConsultationsLoadedByPatientId(state, patientId)
      : false,
  );
  const loading = useAppSelector(selectTranscriptorLoading);
  const error = useAppSelector(selectTranscriptorError);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const failedPollCountRef = useRef(0);

  const refetch = useCallback(() => {
    if (patientId) {
      dispatch(
        fetchConsultations({ patientId, includeArchived: includeHidden }),
      );
    }
  }, [patientId, includeHidden]);

  const isFlagKnown = transcriptorV2Enabled !== undefined;
  const isVisible = (consultation: TranscriptorConsultation) =>
    isFlagKnown && (!consultation.transcriptorV2 || transcriptorV2Enabled);
  const consultations = storedConsultations.filter(isVisible);
  const hiddenConsultations = storedHiddenConsultations.filter(isVisible);

  const hasActiveConsultations = consultations.some((consultation) =>
    ACTIVE_STATUSES.includes(consultation.status),
  );

  const hasFailedConsultations = consultations.some(
    (consultation) => consultation.status === 'failed',
  );

  const shouldPoll =
    hasActiveConsultations ||
    (hasFailedConsultations && failedPollCountRef.current < MAX_FAILED_POLLS);

  useEffect(() => {
    if (patientId) {
      dispatch(
        fetchConsultations({ patientId, includeArchived: includeHidden }),
      );
    }
  }, [patientId, includeHidden]);

  // Reset failed poll counter when active consultations appear
  useEffect(() => {
    if (hasActiveConsultations) {
      failedPollCountRef.current = 0;
    }
  }, [hasActiveConsultations]);

  useEffect(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    if (patientId && shouldPoll) {
      pollIntervalRef.current = setInterval(() => {
        if (!hasActiveConsultations && hasFailedConsultations) {
          failedPollCountRef.current += 1;
        }

        dispatch(
          fetchConsultations({ patientId, includeArchived: includeHidden }),
        );
      }, ENCOUNTER_POLLING_INTERVAL_MS);
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [
    patientId,
    shouldPoll,
    hasActiveConsultations,
    hasFailedConsultations,
    includeHidden,
  ]);

  return {
    consultations,
    hiddenConsultations,
    visibleTotal: isFlagKnown
      ? Math.max(
          0,
          visibleTotal - storedConsultations.length + consultations.length,
        )
      : 0,
    isLoaded: isListLoaded && isFlagKnown,
    loading,
    error,
    refetch,
    hasActiveConsultations,
  };
};
